const SHOW_RULERS_KEY = "curva:show-rulers";
const SNAP_TO_GUIDES_KEY = "curva:snap-to-guides";
const ANIMATION_TIMELINE_OPEN_KEY = "curva.animation.timelineOpen";
const ANIMATION_PREVIEW_WINDOW_OPEN_KEY = "curva.animation.previewWindowOpen";
const MCP_HOST_CONNECT_KEY = "curva:mcp-host-connect";
const SCULPT_BRUSH_KEY = "curva:sculpt-brush";

export interface SculptBrushPreference {
  size: number;
  strength: number;
}

const DEFAULT_SCULPT_BRUSH: SculptBrushPreference = { size: 80, strength: 50 };

const memoryStore = new Map<string, string>();

function storage(): Storage | undefined {
  try {
    return typeof window !== "undefined" ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

function readBool(key: string, fallback: boolean): boolean {
  const raw = storage()?.getItem(key) ?? memoryStore.get(key);
  if (raw === "true") {
    return true;
  }
  if (raw === "false") {
    return false;
  }
  return fallback;
}

function writeBool(key: string, value: boolean): void {
  const serialized = String(value);
  memoryStore.set(key, serialized);
  storage()?.setItem(key, serialized);
}

function clampSculptBrush(value: Partial<SculptBrushPreference>): SculptBrushPreference {
  return {
    size: Math.min(1000, Math.max(2, Number.isFinite(value.size) ? Number(value.size) : DEFAULT_SCULPT_BRUSH.size)),
    strength: Math.min(100, Math.max(1, Number.isFinite(value.strength) ? Number(value.strength) : DEFAULT_SCULPT_BRUSH.strength)),
  };
}

export function readShowRulersPreference(): boolean {
  return readBool(SHOW_RULERS_KEY, false);
}

export function writeShowRulersPreference(show: boolean): void {
  writeBool(SHOW_RULERS_KEY, show);
}

export function readSnapToGuidesPreference(): boolean {
  return readBool(SNAP_TO_GUIDES_KEY, true);
}

export function writeSnapToGuidesPreference(snap: boolean): void {
  writeBool(SNAP_TO_GUIDES_KEY, snap);
}

export function readAnimationTimelineOpenPreference(): boolean {
  return readBool(ANIMATION_TIMELINE_OPEN_KEY, false);
}

export function writeAnimationTimelineOpenPreference(open: boolean): void {
  writeBool(ANIMATION_TIMELINE_OPEN_KEY, open);
}

export function readAnimationPreviewWindowOpenPreference(): boolean {
  return readBool(ANIMATION_PREVIEW_WINDOW_OPEN_KEY, true);
}

export function writeAnimationPreviewWindowOpenPreference(open: boolean): void {
  writeBool(ANIMATION_PREVIEW_WINDOW_OPEN_KEY, open);
}

export function readMcpHostConnectPreference(): boolean {
  return readBool(MCP_HOST_CONNECT_KEY, false);
}

export function writeMcpHostConnectPreference(connect: boolean): void {
  writeBool(MCP_HOST_CONNECT_KEY, connect);
}

export function readSculptBrushPreference(): SculptBrushPreference {
  const raw = storage()?.getItem(SCULPT_BRUSH_KEY) ?? memoryStore.get(SCULPT_BRUSH_KEY);
  if (!raw) {
    return { ...DEFAULT_SCULPT_BRUSH };
  }
  try {
    const parsed = JSON.parse(raw) as Partial<SculptBrushPreference>;
    return clampSculptBrush(parsed ?? {});
  } catch {
    return { ...DEFAULT_SCULPT_BRUSH };
  }
}

export function writeSculptBrushPreference(brush: SculptBrushPreference): void {
  const next = clampSculptBrush(brush);
  const serialized = JSON.stringify(next);
  memoryStore.set(SCULPT_BRUSH_KEY, serialized);
  storage()?.setItem(SCULPT_BRUSH_KEY, serialized);
}

const ALLOW_SCRIPTS_KEY = "curva:allow-scripts";
const SCRIPTS_DEBUG_WINDOW_OPEN_KEY = "curva:scripts-debug-window-open";

export function readAllowScriptsPreference(): boolean {
  return readBool(ALLOW_SCRIPTS_KEY, true);
}

export function writeAllowScriptsPreference(allow: boolean): void {
  writeBool(ALLOW_SCRIPTS_KEY, allow);
}

export function readScriptsDebugWindowOpenPreference(): boolean {
  return readBool(SCRIPTS_DEBUG_WINDOW_OPEN_KEY, false);
}

export function writeScriptsDebugWindowOpenPreference(open: boolean): void {
  writeBool(SCRIPTS_DEBUG_WINDOW_OPEN_KEY, open);
}
